import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, ChevronLeft, ChevronRight, Edit, Plus } from 'lucide-react'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'

type RoleRow = {
  id: number
  roleName: string
  description: string
  status: 'Active' | 'Inactive'
  createdDate: string
}

const MOCK_ROLES: RoleRow[] = [
  { id: 1, roleName: 'Admin', description: '', status: 'Active', createdDate: '2022-11-14' },
  { id: 2, roleName: 'Super Admin', description: '', status: 'Active', createdDate: '2022-11-14' },
  { id: 4, roleName: 'Employee', description: 'Simple Employee', status: 'Active', createdDate: '2022-12-10' },
  { id: 5, roleName: 'HOD', description: '', status: 'Active', createdDate: '2023-10-12' },
]

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  } catch {
    return dateStr
  }
}

export const RolesList: React.FC = () => {
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return MOCK_ROLES
    const q = searchQuery.toLowerCase()
    return MOCK_ROLES.filter((r) =>
      `${r.id} ${r.roleName} ${r.description} ${r.status} ${r.createdDate}`.toLowerCase().includes(q)
    )
  }, [searchQuery])

  const totalRecords = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
  const start = (pageNumber - 1) * pageSize
  const paginatedRows = filteredRows.slice(start, start + pageSize)

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Roles"
        description="Roles Detail"
        action={{ label: 'Add Role', onClick: () => navigate('/security/roles/new'), icon: Plus }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPageNumber(1)
                }}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Id</TableHead>
                <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Role Name</TableHead>
                <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Description</TableHead>
                <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Status</TableHead>
                <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Created Date</TableHead>
                <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-24">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No data available in table
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row) => (
                  <TableRow key={row.id} className="border-b hover:bg-primary/5">
                    <TableCell className="px-4 py-2 align-middle">{row.id}</TableCell>
                    <TableCell className="px-4 py-2 align-middle">{row.roleName}</TableCell>
                    <TableCell className="px-4 py-2 align-middle">{row.description || '—'}</TableCell>
                    <TableCell className="px-4 py-2 text-center align-middle">
                      <Badge variant={row.status === 'Active' ? 'default' : 'secondary'}>{row.status}</Badge>
                    </TableCell>
                    <TableCell className="px-4 py-2 align-middle">{formatDate(row.createdDate)}</TableCell>
                    <TableCell className="px-4 py-2 text-center align-middle">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary hover:bg-primary/10"
                        onClick={() => navigate(`/security/roles/${row.id}`)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              Showing {totalRecords === 0 ? 0 : start + 1} to {Math.min(start + pageSize, totalRecords)} of {totalRecords}{' '}
              entries
            </div>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPageNumber(1)
                }}
                className="h-8 rounded-md border bg-background px-2 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <Button variant="outline" size="sm" disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm">Page {pageNumber} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
